/**
 * Guarded payload construction for doctor "recolor" saves.
 *
 * WHY THIS IS A SEPARATE MODULE: `PUT /allocations` is delete-and-rewrite — the backend
 * deletes every allocation overlapping [startTimeUtc, endTimeUtc] and recreates only what
 * the request body contains (backend/src/update-allocations.ts). So sending a short or
 * empty payload does not "update nothing", it DELETES the day.
 *
 * That makes the decision "may we write, and with what?" the safety-critical part of the
 * feature, and it was previously inlined in two components that had already drifted apart:
 * BoardView kept a `touched` guard, its AnesthesiologistsView twin had lost it. Keeping the
 * rule here, tested, means a third caller cannot reintroduce the bug.
 */

/** The minimal allocation shape this logic needs; both callers' types structurally satisfy it. */
export interface RecolorableAllocation {
  operatingRoomScheduleId: string;
  allocatedNurseIds?: string[];
  allocatedDoctorIds?: string[];
  colorOverrides?: Record<string, string>;
}

export interface RecolorPayloadEntry {
  scheduleId: string;
  allocatedNurseIds: string[];
  allocatedDoctorIds: string[];
  colorOverrides: Record<string, string>;
}

export interface RecolorResult {
  /** The allocations with the override applied — for optimistic local state. */
  nextAllocations: RecolorableAllocation[];
  /** The full-window body to PUT. */
  payload: RecolorPayloadEntry[];
}

/**
 * Applies `color` (or clears it when null) for `employeeId` across every allocation that
 * doctor appears on, and returns the FULL-window payload to persist.
 *
 * Returns `null` — meaning "do not write at all" — when writing would be unsafe or pointless:
 *   - `allocationsLoaded` is false: the list may be empty only because the fetch failed, and
 *     writing from it would erase a day that actually has allocations;
 *   - the list is empty: nothing to rewrite, and an empty body is a full-window delete;
 *   - the doctor is on no allocation: there is nothing to recolor, so a write is pure risk.
 */
export function buildRecolorPayload(
  allocations: RecolorableAllocation[],
  employeeId: string,
  color: string | null,
  allocationsLoaded: boolean,
): RecolorResult | null {
  if (!allocationsLoaded) return null;
  if (!allocations.length) return null;

  let touched = false;
  const nextAllocations = allocations.map(a => {
    if (!(a.allocatedDoctorIds || []).includes(employeeId)) return a;
    const colorOverrides = { ...(a.colorOverrides || {}) };
    if (color) colorOverrides[employeeId] = color;
    else delete colorOverrides[employeeId];
    touched = true;
    return { ...a, colorOverrides };
  });
  if (!touched) return null;

  const payload = nextAllocations.map(a => ({
    scheduleId: a.operatingRoomScheduleId,
    allocatedNurseIds: a.allocatedNurseIds || [],
    allocatedDoctorIds: a.allocatedDoctorIds || [],
    colorOverrides: a.colorOverrides || {},
  }));

  return { nextAllocations, payload };
}
